import type { InfiniteData, QueryClient } from '@tanstack/react-query';
import type { NewConversationListItem, NewConversationPage } from '@apis/NewConversationService';

export const chatHistoryQueryKeys = {
  all: ['chatHistory'] as const,
  list: () => ['chatHistory', 'list'] as const,
  full: () => ['chatHistory', 'full'] as const,
};

export type HistoryInfiniteData = InfiniteData<NewConversationPage, string | undefined>;

type HistoryUpdate =
  | { kind: 'insert'; item: NewConversationListItem }
  | { kind: 'remove'; conversationId: string }
  | { kind: 'rename'; conversationId: string; title: string };

/** Keep browse pages and an already-loaded search list in sync, without
 * fabricating a fetched result for an absent cache. Never flatten/repartition
 * pages: their cursors and pageParams still describe the server's boundaries.
 */
export function updateChatHistoryCache(client: QueryClient, update: HistoryUpdate): void {
  const id = update.kind === 'insert' ? update.item.conversation_id : update.conversationId;
  const updateItems = (items: NewConversationListItem[], prepend: boolean) => {
    if (update.kind === 'rename') {
      return items.map(item => item.conversation_id === id ? { ...item, title: update.title } : item);
    }
    const remaining = items.filter(item => item.conversation_id !== id);
    // Insertion moves this ID to the first page, removing copies on later pages.
    return update.kind === 'insert' && prepend ? [update.item, ...remaining] : remaining;
  };

  client.setQueryData<HistoryInfiniteData>(chatHistoryQueryKeys.list(), old => {
    if (!old) return old;
    return {
      ...old,
      pages: old.pages.map((page, index) => ({
        ...page,
        items: updateItems(page.items, index === 0),
      })),
    };
  });
  client.setQueryData<NewConversationListItem[]>(chatHistoryQueryKeys.full(), old =>
    old ? updateItems(old, true) : old,
  );
}

/** Capture Query objects before cancellation yields: clear()/recreation means
 * a different cache generation even though the global history keys are equal.
 */
export async function beginChatHistoryRename(client: QueryClient, conversationId: string, title: string) {
  const cache = client.getQueryCache();
  const targets = [chatHistoryQueryKeys.list(), chatHistoryQueryKeys.full()].map(key => ({
    key, query: cache.find({ queryKey: key, exact: true }),
  }));
  await client.cancelQueries({ queryKey: chatHistoryQueryKeys.all });
  if (targets.some(({ key, query }) => cache.find({ queryKey: key, exact: true }) !== query)) {
    throw new Error('Chat history context changed before rename');
  }

  const snapshots = targets.map(target => ({ ...target, previous: target.query?.state.data }));
  updateChatHistoryCache(client, { kind: 'rename', conversationId, title });
  return snapshots.map(snapshot => ({ ...snapshot, optimistic: snapshot.query?.state.data }));
}

export function rollbackChatHistoryRename(
  client: QueryClient,
  snapshots: Awaited<ReturnType<typeof beginChatHistoryRename>> | undefined,
): void {
  for (const { key, query, previous, optimistic } of snapshots ?? []) {
    // A late failure owns neither a replacement tenant cache nor newer data in
    // the same query. Restore each cache independently, only while still ours.
    if (query && previous !== undefined &&
        client.getQueryCache().find({ queryKey: key, exact: true }) === query &&
        query.state.data === optimistic) {
      client.setQueryData(key, previous);
    }
  }
}
